import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, TestTube2, Trophy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ABTest {
  id: string;
  test_name: string;
  page_url: string;
  status: string;
  winner: string | null;
  started_at: string;
  completed_at: string | null;
}

export const ABTesting = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testName, setTestName] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [variantA, setVariantA] = useState('');
  const [variantB, setVariantB] = useState('');
  const [tests, setTests] = useState<ABTest[]>([]);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    const { data, error } = await supabase
      .from('seo_ab_tests')
      .select('*')
      .order('started_at', { ascending: false });

    if (error) {
      console.error('Error fetching tests:', error);
    } else {
      setTests(data || []);
    }
  };

  const handleCreateTest = async () => {
    if (!testName || !pageUrl || !variantA || !variantB) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-automation', {
        body: {
          action: 'create_ab_test',
          data: {
            testName,
            pageUrl,
            variantA: { description: variantA },
            variantB: { description: variantB }
          }
        }
      });

      if (error) throw error;

      toast({
        title: 'Test Created',
        description: 'A/B test is now running',
      });

      await fetchTests();
      setTestName('');
      setPageUrl('');
      setVariantA('');
      setVariantB('');
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to create A/B test',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube2 className="w-5 h-5" />
            User Experience & A/B Testing
          </CardTitle>
          <CardDescription>
            Create and monitor A/B tests to optimize SEO performance
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="testName">Test Name</Label>
            <Input
              id="testName"
              placeholder="e.g., Homepage Title Test"
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pageUrl">Page URL</Label>
            <Input
              id="pageUrl"
              placeholder="https://example.com/page"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="variantA">Variant A Description</Label>
              <Textarea
                id="variantA"
                placeholder="Describe variant A..."
                value={variantA}
                onChange={(e) => setVariantA(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="variantB">Variant B Description</Label>
              <Textarea
                id="variantB"
                placeholder="Describe variant B..."
                value={variantB}
                onChange={(e) => setVariantB(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <Button onClick={handleCreateTest} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Test...
              </>
            ) : (
              <>
                <TestTube2 className="w-4 h-4 mr-2" />
                Create A/B Test
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Active Tests ({tests.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {tests.map((test) => (
              <Card key={test.id}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h4 className="font-semibold">{test.test_name}</h4>
                      <p className="text-sm text-muted-foreground">{test.page_url}</p>
                      <p className="text-xs text-muted-foreground">
                        Started: {new Date(test.started_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Badge variant={test.status === 'running' ? 'default' : 'secondary'}>
                        {test.status}
                      </Badge>
                      {test.winner && (
                        <Badge className="bg-green-500">
                          <Trophy className="w-3 h-3 mr-1" />
                          Winner: {test.winner}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {tests.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No A/B tests yet. Create one above.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};